/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */

import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import InsightsIcon from '@mui/icons-material/Insights';
import { Feed, Home, SsidChart } from '@mui/icons-material';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import {
  DataExchangeAgreement,
  DataExchangeAgreementMap,
  HomeQuadrantTabId,
  IndicesContentType,
  EquityFieldKey,
  SubLevel,
} from '../types';
import { ProductType } from 'types/shared';
import CalculateIcon from '@mui/icons-material/Calculate';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BalanceIcon from '@mui/icons-material/Balance';
import CycloneIcon from '@mui/icons-material/Cyclone';
import GestureIcon from '@mui/icons-material/Gesture';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box } from '@mui/material';
import BellCurve from '../icons/BellCurve';
import TapeIcon from '../icons/TapeIcon';
import { memoize } from 'lodash';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SchoolIcon from '@mui/icons-material/School';
import { loadRiskEngineConstants } from 'sg-risk-engine';
import { dayjs } from 'util/shared/date';

export const getRiskEngineConstants = memoize(() =>
  Object.freeze(loadRiskEngineConstants()),
);

export const STREAM_HOST =
  import.meta.env.VITE_STREAMING_HOST ?? 'bbg.stream.spotgamma.com';

export const API_STREAM_HOST =
  import.meta.env.VITE_API_STREAM ?? import.meta.env.VITE_STREAMING_HOST;

export const STREAM_HOST_URL = `https://${API_STREAM_HOST ?? 'bbg.stream.spotgamma.com'}`;

export const BETA_MODE = import.meta.env.VITE_BETA_MODE === 'true';
export const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true';
export const NON_PROD = DEV_MODE || BETA_MODE;

export const BASE_FRONTEND_PATH = (import.meta.env.VITE_BASE_FRONTEND_PATH ??
  '/') as string;

export const MOBILE_MAX_WIDTH = 768; // showing mobile UI on tablets as well

export const HOME_LINK = {
  link: '/home',
  label: 'Market Overview',
  icon: <Home width="24px" />,
};

export const TRAINING_LINK = {
  link: 'https://spotgamma.com/courses/how-to-use-spotgamma/',
  label: 'Training Course',
  icon: <SchoolIcon width="24px" />,
};

export const PRODUCT_LINKS: Record<
  ProductType,
  {
    link: string;
    label: string;
    icon: JSX.Element;
    tagText?: string | null;
    betaOnly?: boolean;
    subLevelRequired?: SubLevel;
  }
> = {
  [ProductType.HOME]: HOME_LINK,
  [ProductType.TRAINING]: TRAINING_LINK,
  [ProductType.EQUITYHUB]: {
    link: '/equityhub',
    label: 'Equity Hub',
    icon: <AlignVerticalCenterIcon width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.SCANNERS]: {
    link: '/scanners',
    label: 'Scanners',
    icon: <QrCodeScannerIcon width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.HIRO]: {
    link: '/hiro',
    label: 'HIRO',
    icon: <InsightsIcon width="24px" />,
    tagText: dayjs() < dayjs('2026-08-01T00:00:00Z') ? 'Updated' : null,
    subLevelRequired: SubLevel.ALPHA,
  },
  [ProductType.INDICES]: {
    link: '/indices',
    label: 'Indices',
    icon: <LeaderboardIcon width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.FOUNDERS_NOTES]: {
    link: '/foundersNotes',
    label: "Founder's Notes",
    icon: <Feed width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.REPORTS]: {
    link: '/reports',
    label: 'Reports',
    icon: <SummarizeIcon width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.INTEGRATIONS]: {
    link: '/integrations',
    label: 'Integrations',
    icon: <ElectricalServicesIcon width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.IMPLIED_VOL]: {
    link: '/ivol',
    label: 'Volatility Dashboard',
    icon: (
      <Box>
        <BellCurve width="24px" />
      </Box>
    ),
    subLevelRequired: SubLevel.ALPHA,
  },
  [ProductType.TAPE]: {
    link: '/tape',
    label: 'Tape',
    icon: <TapeIcon width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.INTERNAL_OPEN_INTEREST]: {
    link: '/oi',
    label: 'Open Interest',
    icon: <BalanceIcon width="24px" />,
    subLevelRequired: SubLevel.NONE,
  },
  [ProductType.INTERNAL_RISK_ENGINE]: {
    link: '/risk',
    label: 'Risk Engine',
    icon: <SsidChart width="24px" />,
    subLevelRequired: SubLevel.NONE,
  },
  [ProductType.OPTIONS_CALCULATOR]: {
    link: '/optionsCalculator',
    label: 'Options Calculator',
    icon: <CalculateIcon width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.TRACE]: {
    link: '/trace',
    label: 'TRACE',
    icon: <CycloneIcon width="24px" />,
    subLevelRequired: SubLevel.ALPHA,
  },
  [ProductType.CANVAS]: {
    tagText: dayjs() < dayjs('2026-08-01T00:00:00Z') ? 'New' : null,
    link: '/canvas',
    label: 'Canvas',
    icon: <GestureIcon width="24px" />,
    subLevelRequired: SubLevel.ESSENTIAL,
  },
  [ProductType.MOVIEFONE]: {
    betaOnly: true,
    link: '/spotline',
    label: 'SpotLine',
    icon: <PhoneIcon width="24px" />,
    subLevelRequired: SubLevel.NONE,
  },
};

export const ALL_PRODUCTS: ProductType[] = [
  ProductType.HOME,
  ProductType.HIRO,
  ProductType.IMPLIED_VOL,
  ProductType.EQUITYHUB,
  ProductType.SCANNERS,
  ProductType.FOUNDERS_NOTES,
  ProductType.REPORTS,
  ProductType.INDICES,
  ProductType.INTEGRATIONS,
  ProductType.INTERNAL_OPEN_INTEREST,
  ProductType.INTERNAL_RISK_ENGINE,
  ProductType.OPTIONS_CALCULATOR,
  ProductType.TRACE,
  ProductType.TAPE,
  ProductType.CANVAS,
  ProductType.MOVIEFONE,
];

export const BLOOMBERG_EXCLUDE_PRODUCTS: ProductType[] = [ProductType.TAPE];

export const DEV_ONLY_PRODUCTS: ProductType[] = [];
export const BETA_ONLY_PRODUCTS: ProductType[] = [ProductType.MOVIEFONE];
export const AFFILIATE_PRODUCTS: ProductType[] = [];
export const INTERNAL_ONLY_PRODUCTS: ProductType[] = [
  ProductType.INTERNAL_OPEN_INTEREST,
  ProductType.INTERNAL_RISK_ENGINE,
];

export const RELEASED_PRODUCTS: ProductType[] = ALL_PRODUCTS.filter(
  (prod: ProductType) =>
    !DEV_ONLY_PRODUCTS.includes(prod) &&
    !BETA_ONLY_PRODUCTS.includes(prod) &&
    !INTERNAL_ONLY_PRODUCTS.includes(prod),
);

export const ESSENTIAL_PRODUCTS: ProductType[] = [
  ProductType.HOME,
  ProductType.FOUNDERS_NOTES,
  ProductType.REPORTS,
  ProductType.EQUITYHUB,
  ProductType.SCANNERS,
  ProductType.INDICES,
  ProductType.TAPE,
  ProductType.OPTIONS_CALCULATOR,
  ProductType.INTEGRATIONS,
  ProductType.CANVAS,
];

export const ONLY_PRO_PRODUCTS: ProductType[] = [ProductType.IMPLIED_VOL];

export const PRO_PRODUCTS: ProductType[] =
  ONLY_PRO_PRODUCTS.concat(ESSENTIAL_PRODUCTS);

export const ONLY_ALPHA_PRODUCTS: ProductType[] = [
  ProductType.HIRO,
  ProductType.TRACE,
];

export const ALPHA_PRODUCTS: ProductType[] =
  ONLY_ALPHA_PRODUCTS.concat(PRO_PRODUCTS);

export const SUBSCRIPTION_PRODUCTS: Record<SubLevel, ProductType[]> = {
  [SubLevel.NONE]: [],
  [SubLevel.ESSENTIAL]: ESSENTIAL_PRODUCTS.filter(
    (pt: ProductType) => pt !== ProductType.HOME,
  ),
  [SubLevel.PRO]: ONLY_PRO_PRODUCTS,
  [SubLevel.ALPHA]: ONLY_ALPHA_PRODUCTS,
};

// On DEV, what access to products do users have?
export const DEV_ACCESS_TO_PRODUCTS: Record<string, ProductType[]> = {
  affiliate: [],
  beta: [],
  internal: ALL_PRODUCTS,
  override: ALL_PRODUCTS,
  '': [],
};

// On BETA (staging), what access to products do users have?
export const BETA_ACCESS_TO_PRODUCTS: Record<string, ProductType[]> = {
  affiliate: ALL_PRODUCTS,
  beta: ALL_PRODUCTS,
  internal: ALL_PRODUCTS,
  override: ALL_PRODUCTS,
  '': [],
};

// On PROD, what access to products do users have?
export const PROD_ACCESS_TO_PRODUCTS: Record<string, ProductType[]> = {
  affiliate: RELEASED_PRODUCTS,
  beta: RELEASED_PRODUCTS,
  internal: ALL_PRODUCTS,
  override: RELEASED_PRODUCTS,
  '': RELEASED_PRODUCTS,
};

export const ZENDESK_SETTINGS = {
  position: {
    horizontal: 'right',
    vertical: 'bottom',
  },
  offset: { horizontal: '0px', vertical: '0px' },
};

export const RIGHT_PROPS = {
  flexDirection: 'row-reverse',
  justifyContent: 'flex-end',
};
export const LEFT_PROPS = { alignItems: 'left' };

export const DEFAULT_POSITIVE_TREND_COLOR = '#18bdac';

export const DEFAULT_NEGATIVE_TREND_COLOR = '#cc304a';

export const FREE_SYMBOLS = ['TSLA', 'AAPL', 'NVDA', 'MSFT', 'AMZN'];

export const MAIN_CONTENT_DEFAULT_WIDTH = 0.7;

export const historyOnlyFields = [
  EquityFieldKey.quoteDate,
  EquityFieldKey.putDelta,
  EquityFieldKey.callDelta,
];

export const scannerOnlyFields = [
  'isWatchlisted',
  EquityFieldKey.sym,
  EquityFieldKey.price,
  EquityFieldKey.stock_volume,
  EquityFieldKey.wkHigh52,
  EquityFieldKey.wkLow52,
  EquityFieldKey.earningsDate,
  EquityFieldKey.dpi5day,
];

export const scannerFields = Object.values(EquityFieldKey).filter(
  (sck) => !historyOnlyFields.includes(sck),
);
export const historyFields = Object.values(EquityFieldKey).filter(
  (sck) => !scannerOnlyFields.includes(sck),
);

export const scannerUnmodifiableFields = [
  'isWatchlisted',
  EquityFieldKey.sym,
  EquityFieldKey.quoteDate,
];

export const DATA_EXCHANGE_AGREEMENT_QUESTIONS: Record<
  DataExchangeAgreement,
  string
> = {
  [DataExchangeAgreement.INSTITUTIONAL_TRADER]:
    'Are you an institutional trader?',
  [DataExchangeAgreement.NON_BUSINESS_DATA_USAGE]:
    'Do you use stock market data exclusively for personal, non-business use?',
  [DataExchangeAgreement.REGISTERED_BODY]:
    'Are you registered or qualified with the SEC, CFTC, any securities agency, exchange, association or regulatory body?',
  [DataExchangeAgreement.ASSET_MANAGER]:
    'Are you an asset manager or investment advisor?',
  [DataExchangeAgreement.EXTERNAL_CAPITAL_USE]:
    'Do you use the capital of any other individual or entity in your trading?',
  [DataExchangeAgreement.TRADING_FOR_CORP]:
    'Do you conduct trading for the benefit of a corporation, partnership, or other entity?',
  [DataExchangeAgreement.IS_PROFIT_SHARING]:
    'Have you entered into any profit-sharing agreement related to your trading activities?',
  [DataExchangeAgreement.BENEFIT_EXCHANGE_FOR_TRADING]:
    'Are you receiving office space, equipment or other benefits in exchange for trading or financial consulting work?',
};

export const DEFAULT_AGREEMENT_FORM: DataExchangeAgreementMap = {
  [DataExchangeAgreement.INSTITUTIONAL_TRADER]: null,
  [DataExchangeAgreement.NON_BUSINESS_DATA_USAGE]: null,
  [DataExchangeAgreement.REGISTERED_BODY]: null,
  [DataExchangeAgreement.ASSET_MANAGER]: null,
  [DataExchangeAgreement.EXTERNAL_CAPITAL_USE]: null,
  [DataExchangeAgreement.TRADING_FOR_CORP]: null,
  [DataExchangeAgreement.IS_PROFIT_SHARING]: null,
  [DataExchangeAgreement.BENEFIT_EXCHANGE_FOR_TRADING]: null,
};

export const CONTENT_TYPE_LABEL_MAP: Map<string, string> = new Map<
  string,
  string
>([
  // indices content
  [IndicesContentType.REAL_TIME_LEVELS, 'Real Time Updates'],
  [IndicesContentType.GAMMA_LEVELS, 'Absolute Gamma'],
  [IndicesContentType.TILT, 'Gamma Tilt Chart'],
  [IndicesContentType.EXP_CONCENTRATION, 'Expiration Concentration'],
  [IndicesContentType.COMBO_STRIKES, 'Combo Strikes'],
  [IndicesContentType.ZERO_DTE, '0DTE Volume/Open Interest'],
  [IndicesContentType.GAMMA_MODEL, 'Gamma Model'],
  [IndicesContentType.VANNA_MODEL, 'Vanna Model'],
  [IndicesContentType.DELTA_MODEL, 'Delta Model'],
  [IndicesContentType.VOLFORECAST_MODEL, 'SIV Index'],
  [IndicesContentType.OI_VOLUME, 'Open Interest & Volume'],
  [IndicesContentType.EXP_CONCENTRATION_TABLE, 'Concentration Table'],
  [IndicesContentType.STRIKE_CONCENTRATION_TABLE, 'Strike Table'],
  [IndicesContentType.OPTIONS_RISK_REVERSAL_CHART, 'Options Risk Reversal'],
  [IndicesContentType.HISTORICAL_CHART, 'Historical Chart'],
  [IndicesContentType.MAX_REAL_VOL, 'Price vs Realized Volatility'],
  [IndicesContentType.HIST_RETURNS, '5 Day & 1 Month Return Histogram'],
  [IndicesContentType.VOLATILITY, 'Realized Volatility'],
  // home only content
  [HomeQuadrantTabId.LIVE_PRICES_CHART, 'Live Prices'],
  [HomeQuadrantTabId.SG_LEVELS, 'SpotGamma Levels'],
  [HomeQuadrantTabId.INDEX_LEVELS, 'Index Levels'],
  [HomeQuadrantTabId.AT_SPOTGAMMA, '@ SpotGamma'],
  [HomeQuadrantTabId.MACRO_ECONOMIC_CALENDAR, 'Events Calendar'],
  [HomeQuadrantTabId.EARNINGS, 'Earnings'],
  [HomeQuadrantTabId.EARNINGS_CHART, 'Earnings Chart'],
  [HomeQuadrantTabId.SCANNER_SQUEEZE_CANDIDATES, 'Squeeze Candidates'],
  [HomeQuadrantTabId.SCANNER_VRP_CANDIDATES, 'VRP'],
  [HomeQuadrantTabId.INDEX_METRICS, 'Index Metrics'],
  [HomeQuadrantTabId.SUPP_RESISTANCE_LEVELS, 'Support & Resistance'],
  [HomeQuadrantTabId.OCC_CHART, 'Options Clearning Corporation'],
  [HomeQuadrantTabId.ZERO_DTE_EQUITY_VOL_CHART, '0DTE Equity Volume/OI'],
  [HomeQuadrantTabId.EQUITY_PUT_CALL_RATIO_CHART, 'Equity Put/Call Ratio'],
  [HomeQuadrantTabId.EXP_CONCENTRATION_CHART, 'Expiration Concentration'],
  [HomeQuadrantTabId.VIX_CHART, 'VIX Term Structure'],
]);

export const USING_NEW_MODEL = import.meta.env.VITE_SPOTGAMMA_API!.includes(
  'dev.api.spotgamma',
);

export const SEEN_OH_UPSELL_KEY = 'sg_oh'; // equivalent to spotgamma_openHouse

export const OPEN_HOUSE_UPSELL = {
  title: "WELCOME TO SPOTGAMMA'S OPEN HOUSE",
  subtitle: 'FREE ACCESS AVAILABLE TO YOU FOR A LIMITED TIME',
  body: "This week only, enjoy free access to SpotGamma tools and data. We are confident that once you use our tools, you won't go back to trading in the dark. Your exclusive SpotGamma access ends Thursday, October 30. For continued access to SpotGamma, subscribe now and get 50% OFF your first year.",
};

export const EQUITIES_TOOLTIP_MAP = new Map<EquityFieldKey, string>([
  [
    EquityFieldKey.price,
    "Current stock price. If a stock's current price is trading lower than the previous closing price, the price will be shown in red, and if the stock's current price is trading higher than the previous closing price, then it will be shown in green. These colors can be customized so may change based on your preferences.",
  ],
  [
    EquityFieldKey.prevClose,
    "Price of the stock at yesterday's close. Our data is modeled using last night's closing price. If the current price is far from the closing than some of our levels may jump or shift accordingly.",
  ],
  [
    EquityFieldKey.stock_volume,
    'This is the total volume of stock traded the previous day.',
  ],
  [
    EquityFieldKey.wkHigh52,
    'The highest price the stock has traded at in the past 52 weeks.',
  ],
  [
    EquityFieldKey.wkLow52,
    'The lowest price the stock has traded at in the past 52 weeks.',
  ],
  [
    EquityFieldKey.earningsDate,
    'The next scheduled earnings announcement date for this stock.',
  ],
  [
    EquityFieldKey.keyGammaStr,
    'Key gamma levels that may act as support or resistance for the stock price due to options-related hedging flows.',
  ],
  [
    EquityFieldKey.keyDeltaStr,
    'Key delta levels that may act as support or resistance for the stock price due to options-related hedging flows.',
  ],
  [
    EquityFieldKey.hedgeWall,
    'A significant concentration of options positions that may create strong support or resistance due to dealer hedging.',
  ],
  [
    EquityFieldKey.callWall,
    'A significant concentration of call options that may create resistance due to dealer hedging.',
  ],
  [
    EquityFieldKey.putWall,
    'A significant concentration of put options that may create support due to dealer hedging.',
  ],
  [
    EquityFieldKey.callGamma,
    'The total gamma exposure from call options, indicating potential hedging needs as price changes.',
  ],
  [
    EquityFieldKey.putGamma,
    'The total gamma exposure from put options, indicating potential hedging needs as price changes.',
  ],
  [
    EquityFieldKey.callDelta,
    'The total delta exposure from call options, indicating directional exposure.',
  ],
  [
    EquityFieldKey.putDelta,
    'The total delta exposure from put options, indicating directional exposure.',
  ],
  [
    EquityFieldKey.nextExpGamma,
    'Percentage of gamma expiring at the next expiration. When the amount of gamma expiring is above 25% it means there is a significant concentration of short dated options positions in the stock. This may magnify the importance of options positioning due to expiration.',
  ],
  [
    EquityFieldKey.nextExpDelta,
    'Percentage of delta expiring at the next expiration. When the amount of delta expiring is above 25% it means there is a significant concentration of short dated options positions in the stock. This may magnify the importance of options positioning due to expiration.',
  ],
  [
    EquityFieldKey.topGammaExp,
    'Expiration date with the largest gamma notional expiring. Dates with large options expiring may invoke significant options related hedging flows. This suggests that the impact of options related flows may be a key driver of the stock in the days into and out of options expiration.',
  ],
  [
    EquityFieldKey.topDeltaExp,
    'Expiration date with the largest delta notional expiring. Dates with large options expiring may invoke significant options related hedging flows. This suggests that the impact of options related flows may be a key driver of the stock in the days into and out of options expiration.',
  ],
  [
    EquityFieldKey.callVolume,
    'Total volume of call options traded on the previous day.',
  ],
  [
    EquityFieldKey.putVolume,
    'Total volume of put options traded on the previous day.',
  ],
  [
    EquityFieldKey.neCallVolume,
    'The percentage of the previous sessions total call volume that was traded in contracts which expire at the next available expiration.',
  ],
  [
    EquityFieldKey.nePutVolume,
    'The percentage of the previous sessions total put volume that was traded in contracts which expire at the next available expiration.',
  ],
  [
    EquityFieldKey.putCallRatio,
    'Number of puts open interest yesterday, vs number of call open interest for the previous session.',
  ],
  [
    EquityFieldKey.putCallVolRatio,
    'The total volume of puts divided by the total volume of calls, based on the previous trading day volume',
  ],
  [EquityFieldKey.gammaRatio, 'Total call gamma divided by total put gamma.'],
  [EquityFieldKey.deltaRatio, 'Total call delta divided by total put delta.'],
  [
    EquityFieldKey.neSkew,
    'Implied volatility skew for options expiring on the next expiration date. A high NE Skew reading indicates short-term call demand is high relative to put demand. If NE Skew significantly exceeds Skew, that indicates heavy short-term options trading activity.',
  ],
  [
    EquityFieldKey.skew,
    'Implied volatility skew for options expiring in approximately one month. A high Skew reading indicates call demand is high relative to put demand.',
  ],
  [
    EquityFieldKey.rv30,
    'This represents 1-month realized volatility, or actual volatility experienced by an underlying asset over one month.',
  ],
  [
    EquityFieldKey.iv30,
    'This represents Implied Volatility, or market expectation of volatility, for the following month.',
  ],
  [
    EquityFieldKey.ivRank,
    "This rank displays how high a stock's current IV is versus the prior year.",
  ],
  [
    EquityFieldKey.garchRank,
    "The Garch Rank is a proprietary measure of how a stock's volatility compares to the prior 30 days, ignoring event-specific volume.",
  ],
  [
    EquityFieldKey.skewRank,
    "Display how high a stock's most recent skew (difference between 25 delta call IV / put IV) ranks versus the prior year; high values suggest traders hold a bullish view of the stock, while lower values indicate bearish expectations.",
  ],
  [
    EquityFieldKey.optionsImpliedMove,
    'Estimated stock move based on current options implied volatility. This is the percentage move options traders are pricing in for the stock over the next 30 days. If you believe the stock may move more or less than the estimated move, you may have a trading edge.',
  ],
  [
    EquityFieldKey.dpi,
    'Dark Pool Indicator assess the short activity within a given stock or index. You can see the DPI color scale to quickly monitor names with the highest DPI % in darker green, indicating the most bullish results, and the lowest DPI % in darker red indicating the most bearish results.',
  ],
  [
    EquityFieldKey.dpiVol,
    'The number of shares sold short, both in dark pools and on listed exchanges, divided by the total shares outstanding for a given stock.',
  ],
  [
    EquityFieldKey.dpi5day,
    'The average DPI reading over the last five trading days.',
  ],
  [
    EquityFieldKey.dpi5dayVol,
    'The average DPI % reading over the last five trading days.',
  ],
  [
    EquityFieldKey.high_vol_point,
    'This level holds the least, or most negative local gamma. Heightened volatility can be expected near this level.',
  ],
  [
    EquityFieldKey.low_vol_point,
    'This level holds the most, or most positive local gamma. Price movement would be expected to be relatively low near this level, serving as a potential pin.',
  ],
  [
    EquityFieldKey.optionsImpact,
    'This metric quantifies the hedging pressure for a name relative to the volume traded, measuring how impactful options are for each stock.',
  ],
  [
    EquityFieldKey.cskew_pct,
    'Measures the difference between implied volatility for 25 delta calls and 50 delta calls, indicating how relatively expensive upside may be',
  ],
  [
    EquityFieldKey.pskew_pct,
    'Measures the difference between implied volatility for 25 delta pits and 50 delta puts, indicating how relatively expensive downside may be',
  ],
  [
    EquityFieldKey.iv_pct,
    'Current 30-day implied volatility for at-the-money options.',
  ],
  [EquityFieldKey.callsum, 'Total call open interest for the stock.'],
  [EquityFieldKey.putsum, 'Total put open interest for the stock.'],
]);

export const ALPHA_UPSELL_ITEMS: string[] = [
  'Real-time charts incorporating all options trades',
  'Proprietary & assumption-free data on market maker behavior',
  'Highly actionable alerts for options and equity traders',
];

export const PRODUCT_USER_GUIDES: Map<ProductType, string> = new Map<
  ProductType,
  string
>([
  [
    ProductType.OPTIONS_CALCULATOR,
    'https://spotgamma.com/wp-content/uploads/2025/10/Options-Calculator-User-Guide.pdf',
  ],
  [
    ProductType.EQUITYHUB,
    'https://spotgamma.com/wp-content/uploads/2025/04/SpotGamma-Equity-Hub-User-Guide-1.pdf',
  ],
  [
    ProductType.SCANNERS,
    'https://spotgamma.com/wp-content/uploads/2025/10/Compass-Scanners-User-Guide-October-13-2025.pdf',
  ],
  [ProductType.HIRO, 'https://spotgamma.com/hiro-user-guide'],
  [
    ProductType.TRACE,
    'https://spotgamma.com/wp-content/uploads/2025/07/TRACE-User-Manual-Final-Version.pdf',
  ],
  [
    ProductType.TAPE,
    'https://spotgamma.com/wp-content/uploads/2024/12/SpotGamma-Tape-User-Guide.pdf',
  ],
]);

export const PRODUCT_TRADING_MODULES: Map<ProductType, string> = new Map<
  ProductType,
  string
>([
  [
    ProductType.FOUNDERS_NOTES,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-founders-note/',
  ],
  [
    ProductType.OPTIONS_CALCULATOR,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-options-calculator/',
  ],
  [
    ProductType.INDICES,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-indices/',
  ],
  [
    ProductType.EQUITYHUB,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-equity-hub/',
  ],
  [
    ProductType.SCANNERS,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-scanners/',
  ],
  [
    ProductType.HIRO,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-2/',
  ],
  [
    ProductType.IMPLIED_VOL,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-volatility-dashboard/',
  ],
  [
    ProductType.TRACE,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-trace/',
  ],
  [
    ProductType.TAPE,
    'https://spotgamma.com/courses/how-to-use-spotgamma/lessons/welcome-tape/',
  ],
]);
